import { Cloudinary } from "@cloudinary/url-gen"
import { pause, resume } from "@noriginmedia/norigin-spatial-navigation"
import { useCallback, useState } from "react"
import {
  authRequestWithPassword,
  authRequestWithRefreshToken,
  PasswordCredentials,
} from "src/utilities/auth"
import { CuratedContentSlugV2 } from "src/utilities/common"
import {
  getAccessToken,
  saveAccessToken,
  destroyAccessToken,
  Token,
  getRefreshToken,
  destroyRefreshToken,
  saveRefreshToken,
  destroyExpiresOn,
  getExpiresOn,
  saveExpiresOn,
} from "src/utilities/token"
import { create } from "zustand"

import { Pages } from "../App"
import {
  CuratedContentPageQuery,
  CurrentUserQuery,
  MyStagePageQuery,
} from "../generated/graphql"
import { useMixpanel } from "../hooks/use-mixpanel"
import { CloudinaryConf } from "../utilities/common"
import { MyEventEmitter } from "../utilities/events"

const events = new MyEventEmitter()

export interface DGPrompt {
  open: boolean
  title?: string
  message?: string
  // displays cancel button / default true
  dismissible?: boolean
  // enable back button press close / default true
  canClose?: boolean
  confirmButtonText?: string
  dismissButtonText?: string
  onClose: () => void
  onConfirm?: () => void
  additionalButtons?: { text: string; onPress: () => void; focusKey: string }[]
  icon?: "ERROR" | "ALERT" | "WIFI"
}

type AppReadyPage = CuratedContentSlugV2 | Pages
type AppReady = Partial<Record<AppReadyPage, boolean>>
type ConsentType = "mux" | "mixpanel" | "sentry"
type AccessibilityKeys = { transitions: boolean; high_contrast: boolean }
export type AccessibilityProps = {
  type: keyof AccessibilityKeys
  value: boolean
}

export type SetTrackingProps = { type: ConsentType; value: boolean }
export type SetStreamingProps = { type: "lowData" | "atmos"; value: boolean }
export type GeoInfo = { countryCode?: string }
interface AppState {
  setSpinner: (spinner: "in" | "out" | "soft") => void
  slug?: string
  setSlug: (slug: string) => void
  accessTokenState: Token
  setAccessTokenState: (refreshToken: Token | undefined) => void
  refreshTokenState: Token
  setRefreshTokenState: (refreshToken: Token | undefined) => void
  expiresOnState: Date | undefined
  setExpiresOnState: (expiresOn: Date | undefined) => void
  /*
  currentUser = undefined: the client hasn't attempted or hasn't finished querying the user.
  currentUser = null: client is done querying the user but found none.
   */
  currentUser?: CurrentUserQuery["currentUser"] | null
  setCurrentUser: (currentUser?: CurrentUserQuery["currentUser"] | null) => void
  cld: Cloudinary
  events: MyEventEmitter
  backButtonOverride: string[]
  registerBackButtonOverride: (id: string, action: boolean) => void
  curatedContent: Partial<Record<CuratedContentSlugV2, CuratedContentPageQuery>>
  setCuratedContent: (
    slug: CuratedContentSlugV2,
    data: CuratedContentPageQuery,
  ) => void
  myStage?: MyStagePageQuery
  setMyStage: (myStage?: MyStagePageQuery) => void
  prompt?: DGPrompt
  setPrompt: (prompt?: DGPrompt) => void
  geoInfo?: GeoInfo
  setGeoInfo: (geoInfo?: { countryCode?: string }) => void
  minScreenHeight: number
  setMinScreenHeight: (minScreenHeight: number) => void
  appReady: AppReady
  setAppReady: (query: { page: AppReadyPage; state: boolean }) => void
  tracking: Record<ConsentType, boolean>
  setTracking: (tracking: SetTrackingProps) => void
  streaming: { lowData: boolean; atmos: boolean }
  setStreaming: (streaming: SetStreamingProps) => void
  accessibility: AccessibilityKeys
  setAccessibility: (accessibility: AccessibilityProps) => void
  lastRouteIsBackNav: boolean
  setLastRouteIsBackNav: (lastRouteIsBackNav: boolean) => void
}

const getDefaultTracking = () => {
  const mux = window.localStorage.getItem("mux") !== "false"
  const mixpanel = window.localStorage.getItem("mixpanel") !== "false"
  // DGAPP-6833: sentry got introduced after some users already passed onboarding, hence it must be explicitly set to true
  const sentry = window.localStorage.getItem("sentry") === "true"
  return { mux, mixpanel, sentry }
}
const getDefaultAccessibility = () => {
  const transitions =
    window.localStorage.getItem("dg_accessibility_transitions") !== "false"
  const high_contrast =
    window.localStorage.getItem("dg_accessibility_high_contrast") !== "false"
  return { transitions, high_contrast }
}
const getDefaultStreaming = () => {
  const getDefaultValue = (
    item: string | null,
    defaultValue: boolean = false,
  ) =>
    item === null
      ? defaultValue
      : item === "true"
        ? true
        : item === "false"
          ? false
          : defaultValue
  const lowData = window.localStorage.getItem("lowData")
  const atmos = window.localStorage.getItem("atmos")
  return {
    lowData: getDefaultValue(lowData),
    atmos: getDefaultValue(atmos /*, true*/), //DGAPP-5564: atmos off by default
  }
}

export const appStore = create<AppState>((set, getState) => ({
  // PURE CSS ANIMATIONS in index.html
  setSpinner: (spinner: "in" | "out" | "soft") => {
    const fade = document.getElementById("fader")
    if (fade) {
      if (spinner === "in") {
        pause()
        fade.classList.add("spinner_in")
      } else if (spinner === "soft") {
        pause()
        fade.classList.add("spinner_in_soft")
      } else {
        resume()
        fade.classList.remove("spinner_in")
        fade.classList.remove("spinner_in_soft")
      }
    }
  },
  slug: CuratedContentSlugV2.Discover,
  setSlug: (slug) => set({ slug }),
  accessTokenState: (() => {
    const accessToken = getAccessToken()
    if (accessToken) {
      return accessToken
    }
    return undefined
  })(),
  setAccessTokenState: (accessTokenState) => set(() => ({ accessTokenState })),
  refreshTokenState: (() => {
    const refreshToken = getRefreshToken()
    if (refreshToken) {
      return refreshToken
    }
    return undefined
  })(),
  expiresOnState: (() => {
    const expiresOn = getExpiresOn()
    if (expiresOn) {
      return expiresOn
    }
    return undefined
  })(),
  setExpiresOnState: (expiresOnState) => set({ expiresOnState }),
  setRefreshTokenState: (refreshTokenState) =>
    set(() => ({ refreshTokenState })),
  currentUser: undefined,
  setCurrentUser: (currentUser) => set({ currentUser }),
  cld: new Cloudinary(CloudinaryConf),
  events,
  backButtonOverride: [],
  registerBackButtonOverride: (id, action) => {
    const stack = [...getState().backButtonOverride]
    if (action) {
      if (!stack.includes(id)) set({ backButtonOverride: [...stack, id] })
    } else {
      stack.splice(stack.indexOf(id), 1)
      set({ backButtonOverride: stack })
    }
  },
  curatedContent: {},
  setCuratedContent: (
    slug: CuratedContentSlugV2,
    data: CuratedContentPageQuery,
  ) => set({ curatedContent: { ...getState().curatedContent, [slug]: data } }),
  myStage: undefined,
  setMyStage: (myStage?: MyStagePageQuery) => set({ myStage }),
  prompt: undefined,
  setPrompt: (prompt) => set({ prompt }),
  geoInfo: undefined,
  setGeoInfo: (geoInfo) => set({ geoInfo }),
  minScreenHeight: 20000,
  setMinScreenHeight: (minScreenHeight) => {
    if (minScreenHeight > getState().minScreenHeight)
      set({ minScreenHeight: minScreenHeight + 348 }) // include footer
  },
  appReady: {
    [CuratedContentSlugV2.Discover]: false,
    [CuratedContentSlugV2.Audio]: false,
    [CuratedContentSlugV2.Live]: false,
    [CuratedContentSlugV2.Search]: false,
    MyStage: false,
  },
  setAppReady: (query) => {
    set({ appReady: { ...getState().appReady, [query.page]: query.state } })
  },
  tracking: getDefaultTracking(),
  setTracking: (tracking) => {
    const current = getState().tracking
    set({ tracking: { ...current, [tracking.type]: tracking.value } })
    if (!tracking.value) window.localStorage.setItem(tracking.type, "false")
    else window.localStorage.setItem(tracking.type, "true")
  },
  streaming: getDefaultStreaming(),
  setStreaming: (streaming) => {
    const current = getState().streaming
    set({ streaming: { ...current, [streaming.type]: streaming.value } })
    window.localStorage.setItem(
      streaming.type,
      streaming.value ? "true" : "false",
    )
  },
  accessibility: getDefaultAccessibility(),
  setAccessibility: (accessibility) => {
    const current = getState().accessibility
    set({
      accessibility: { ...current, [accessibility.type]: accessibility.value },
    })
    window.localStorage.setItem(
      "dg_accessibility_" + accessibility.type,
      accessibility.value ? "true" : "false",
    )
  },
  lastRouteIsBackNav: false,
  setLastRouteIsBackNav: (lastRouteIsBackNav: boolean) =>
    set({ lastRouteIsBackNav }),
}))

export function useAuth() {
  const accessTokenState = appStore((state) => state.accessTokenState)
  const setAccessTokenState = appStore((state) => state.setAccessTokenState)
  const refreshTokenState = appStore((state) => state.refreshTokenState)
  const setRefreshTokenState = appStore((state) => state.setRefreshTokenState)
  const setExpiresOnState = appStore((state) => state.setExpiresOnState)
  const setCurrentUser = appStore((state) => state.setCurrentUser)

  const track = useMixpanel()

  const [isFetchingToken, setIsFetchingToken] = useState<boolean>(false)

  const setAccessToken = useCallback(
    (newToken: Token) => {
      // Save/destroy the new token in the cookie first
      if (newToken === undefined) {
        destroyAccessToken()
      } else if (typeof newToken === "string") {
        saveAccessToken(newToken)
      }
      // Then update the state atom
      setAccessTokenState(newToken)
    },
    [setAccessTokenState],
  )

  const setRefreshToken = useCallback(
    (newToken: Token) => {
      // Save/destroy the new token in the cookie first
      if (newToken === undefined) {
        destroyRefreshToken()
      } else if (typeof newToken === "string") {
        saveRefreshToken(newToken)
      }
      // Then update the state atom
      setRefreshTokenState(newToken)
    },
    [setRefreshTokenState],
  )

  const setExpiresOn = useCallback(
    (expiresIn: number | undefined) => {
      let expiresOnDate: Date | undefined
      // Save/destroy the new token in the cookie first
      if (expiresIn === undefined) {
        destroyExpiresOn()
      } else {
        expiresOnDate = new Date(Date.now() + expiresIn * 1000)
        saveExpiresOn(expiresOnDate.toISOString())
      }
      // Then update the state atom
      setExpiresOnState(expiresOnDate)
    },
    [setRefreshTokenState],
  )

  /**
   * Store the access token in the cookie and update the state atom.
   */
  const storeTokens = useCallback(
    ({
      accessToken,
      refreshToken,
      expiresIn,
    }: {
      accessToken: Token
      refreshToken: Token
      expiresIn?: number | undefined
    }) => {
      setAccessToken(accessToken)
      setRefreshToken(refreshToken)
      setExpiresOn(expiresIn)
    },
    [setAccessToken, setRefreshToken, setExpiresOn],
  )

  /**
   * Login with username and password.
   * @param credentials user credentials
   * @returns a promise resolving into a token
   * @throws an error if the login failed
   */
  const login = useCallback(
    async (credentials: PasswordCredentials) => {
      setIsFetchingToken(true)
      const token = await authRequestWithPassword(credentials)
      setIsFetchingToken(false)
      storeTokens(token)
    },
    [storeTokens],
  )

  /**
   * Logout by clearing the access and refresh tokens.
   */
  const logout = useCallback((): void => {
    storeTokens({
      accessToken: undefined,
      refreshToken: undefined,
      expiresIn: undefined,
    })
    setCurrentUser(null)
    track("logout_success")
  }, [storeTokens])

  /**
   * Handle a token expired response from the API by refreshing the access token.
   */
  const onTokenExpired = useCallback(async (): Promise<void> => {
    // Don't do anything if we're already fetching a new token or if we don't have a refresh token
    if (isFetchingToken || refreshTokenState === undefined) return
    setIsFetchingToken(true)
    try {
      // Attempt to refresh the token and store it in the state
      const token = await authRequestWithRefreshToken(refreshTokenState)
      storeTokens(token)
    } catch {
      // If refresh access token fails, log the user out
      logout()
    }
    setIsFetchingToken(false)
  }, [isFetchingToken, refreshTokenState, storeTokens, logout])

  return {
    login,
    logout,
    storeTokens,
    onTokenExpired,
    token: accessTokenState,
    setAccessToken,
    setRefreshToken,
  }
}

export function useIsAuthenticated(): boolean {
  const accessToken = appStore((state) => state.accessTokenState)

  return Boolean(accessToken)
}
